import { Skeleton } from '@chakra-ui/react';
import React, { createContext, useContext, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { DataPipelineNodeData, Edge } from '../types/dataPipelineTypes';
import { useUploadContext } from 'packages/dataPipeline/hooks/UploadContext';

export type InstanceMonitorContext = {
  meshRunId: DataPipeline.MeshRunId;
  meshRunRecord: DataPipeline.MeshRunRecord;
  meshDocument: DataPipeline.MeshDocument;
  nodes: DataPipelineNodeData[];
  edges: Edge[];
  selectedNodeId: string | null;
  setSelectedNode: (nodeId: string | null) => void;
};

const instanceMonitorContext = createContext<InstanceMonitorContext | null>(
  null,
);

export const useInstanceGraph = (): InstanceMonitorContext => {
  const context = useContext(instanceMonitorContext);
  finleyAssert(
    context,
    'useInstanceGraph must be used within a InstanceGraphProvider.',
  );

  return context;
};

export const InstanceGraphProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // use upload context. This provides all the data needed to render the graph.
  // and also handles polling.
  const { meshRunId, instances, completeRecord } = useUploadContext();

  const value = useMemo<InstanceMonitorContext | null>(() => {
    if (!completeRecord || !meshRunId) {
      return null;
    }
    const { meshDocument, meshRunRecord } = completeRecord;

    const artifactIdToInstanceMap = new Map<string, DataPipeline.Instance>();
    instances.forEach((instance) =>
      artifactIdToInstanceMap.set(instance.artifactId, instance),
    );

    const edges = meshDocument.dependencies ?? [];
    const nodes = meshDocument.artifacts.map((artifact) => ({
      artifact,
      instance: artifactIdToInstanceMap.get(artifact.id) || null,
    }));

    return {
      meshRunId,
      nodes,
      edges,
      meshDocument,
      meshRunRecord,
      selectedNodeId,
      setSelectedNode: setSelectedNodeId,
    };
  }, [completeRecord, instances, meshRunId, selectedNodeId]);

  if (!value) {
    return <Skeleton height="600px" />;
  }

  return (
    <instanceMonitorContext.Provider value={value}>
      {value && children}
    </instanceMonitorContext.Provider>
  );
};

export type DisplayNode = {
  displayId: string;
  hasInputs: boolean;
  hasOutputs: boolean;
  artifact: DataPipeline.Artifact;
  instance: DataPipeline.Instance | null;
  isSelected: boolean;
};

export const useNode = (nodeId: string): DisplayNode | null => {
  const { edges, nodes, selectedNodeId } = useInstanceGraph();
  const node = nodes.find(({ artifact }) => artifact.id === nodeId);
  if (!node) {
    return null;
  }

  const hasInputs = edges.some((edge) => edge.target === nodeId);
  const hasOutputs = edges.some((edge) => edge.source === nodeId);

  // generate a node displayId for the children to help manage state.
  const displayId = `displayNode_${node.artifact.id}`;
  return {
    displayId,
    hasInputs,
    hasOutputs,
    artifact: node.artifact,
    instance: node.instance,
    isSelected: selectedNodeId === node.artifact.id,
  };
};

export const useMeshDocument = (): DataPipeline.MeshDocument => {
  const { meshDocument } = useInstanceGraph();
  return meshDocument;
};

export const useMeshRunRecord = (): DataPipeline.MeshRunRecord => {
  const { meshRunRecord } = useInstanceGraph();
  return meshRunRecord;
};
